import React, { useMemo } from 'react'
import { useNewTranslation } from 'hooks/index'
import { useSelector } from 'react-redux'
import { Box, Text } from 'rebass'
import { State } from 'state'
import { TagOption } from 'state/constants'
import {
    makeCommentTagBadgesSelector,
    selectSelectedSubmissionView,
    makeShowCommentSelector,
    SubmissionProgressProps,
} from 'state/review'
import { SUBMISSION_VIEW_ENUM } from 'state/submissions'
import { StepState } from 'state/tasks'
import { theme } from 'theme'
import RoundTag, { TagContentType } from 'particles/Tag'

interface CommentProps {
    submissionProgress: SubmissionProgressProps
    step: StepState
}

const Comment: React.FC<CommentProps> = ({ submissionProgress, step }) => {
    const [newT] = useNewTranslation()
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectShowComment = useMemo(makeShowCommentSelector, [])
    const showComment = useSelector((state: State) => selectShowComment(state, { step }))
    const selectTagBadges = useMemo(makeCommentTagBadgesSelector, [])
    const tagBadges = useSelector((state: State) => selectTagBadges(state, { submissionProgress }))

    if (!showComment) {
        return null
    }
    let title = ''
    let contentType: TagContentType = 'neutral'
    let comment = submissionProgress.comment

    if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED) {
        title = newT('submission.review.reasonsForRequestEditTitle', 'Reasons for Requesting Edits')
        contentType = 'warning'
    } else if (selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED) {
        title = newT('submission.review.approvalCommentHeader', 'Approval comments')
        contentType = 'positive'
    } else if (
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW &&
        submissionProgress.pending_action !== null
    ) {
        const { pending_action: pendingAction } = submissionProgress
        if (pendingAction.action_type === '1') {
            // Pending approve
            contentType = 'positive'
            title = newT('submission.review.approvalCommentHeader', 'Approval comments')
        } else if (pendingAction.action_type === '3') {
            // Pending request edits
            contentType = 'warning'
            title = newT('submission.review.reasonsForRequestEditTitle', 'Reasons for Requesting Edits')
        } else if (pendingAction.action_type === '2') {
            // Pending reject
            contentType = 'negative'
            title = newT('submission.review.reasonsForRejectionTitle', 'Reasons for Rejection')
        }
        comment = pendingAction.data.comment
    }

    return (
        <Box
            sx={{
                borderTop: `1px solid ${theme.colors.governorBay}`,
                borderBottom: `1px solid ${theme.colors.governorBay}`,
            }}
        >
            <Text
                fontSize="0.8em"
                fontWeight="bold"
                color={contentType}
                textAlign="left"
                mt={theme.spacing.xxxs}
                mb={theme.spacing.xxxxs}
            >
                {title}
            </Text>
            {tagBadges && tagBadges.length > 0 && (
                <Box sx={{ textAlign: 'left' }}>
                    {tagBadges.map((tag: TagOption) => {
                        return <RoundTag key={tag.uuid} contentType={contentType} label={tag.label} />
                    })}
                </Box>
            )}
            <Text
                fontSize="0.8em"
                color={theme.colors.mineShaft}
                textAlign="left"
                mt={theme.spacing.xxxxs}
                mb={theme.spacing.xxxs}
            >
                {comment ? comment : newT('submission.review.noSubmissionComment', 'None')}
            </Text>
        </Box>
    )
}

export default Comment
